import React, { useState, useEffect } from "react";
import { useParams, useOutletContext, useNavigate } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Save, Loader2, User, Shield, Palette, Wifi, WifiOff, Bot, Users, LogIn, GitBranch, Terminal, UsersRound, Smartphone, Plus, Trash2 } from "lucide-react";
import MediaUploadInput from "@/components/MediaUploadInput";
import ClientWorkspacesPanel from "@/pages/admin/ClientWorkspacesPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminClientDetail() {
  const { id } = useParams();
  const { getAdminAxios } = useOutletContext();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [profile, setProfile] = useState({ name: "", email: "", business_name: "" });
  const [newPassword, setNewPassword] = useState("");
  const [passwordSaved, setPasswordSaved] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [perms, setPerms] = useState({});
  const [branding, setBranding] = useState({});
  const [botSettings, setBotSettings] = useState({});
  const [groupSettings, setGroupSettings] = useState({});
  const [flows, setFlows] = useState([]);
  const [groupCmds, setGroupCmds] = useState([]);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    getAdminAxios().get(`${API}/admin/clients/${id}`)
      .then(({ data }) => {
        setClient(data);
        setProfile({ name: data.name || "", email: data.email || "", business_name: data.business_name || "" });
        setPerms(data.permissions || {});
        setBranding(data.branding || {});
        setBotSettings(data.bot_settings || {});
        setGroupSettings(data.group_settings || {});
      })
      .catch(() => navigate("/admin/clients"))
      .finally(() => setLoading(false));
    // Fetch flows, commands, groups
    getAdminAxios().get(`${API}/admin/clients/${id}/flows`).then(({ data }) => setFlows(data)).catch(() => {});
    getAdminAxios().get(`${API}/admin/clients/${id}/group-commands`).then(({ data }) => setGroupCmds(data)).catch(() => {});
    getAdminAxios().get(`${API}/admin/clients/${id}/groups`).then(({ data }) => setGroups(data)).catch(() => {});
  }, [id]);

  const save = async (section, endpoint, payload) => {
    setSaving((p) => ({ ...p, [section]: true }));
    try {
      await getAdminAxios().put(`${API}/admin/clients/${id}/${endpoint}`, payload);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving((p) => ({ ...p, [section]: false }));
    }
  };

  const handleImpersonate = async () => {
    try {
      const { data } = await getAdminAxios().post(`${API}/admin/impersonate/${id}`);
      localStorage.setItem("wa_token", data.token);
      window.open("/dashboard", "_blank");
    } catch (err) {
      console.error(err);
    }
  };

  const resetPassword = async () => {
    setPasswordError("");
    setPasswordSaved(false);
    if (!newPassword || newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return;
    }
    setSaving((p) => ({ ...p, password: true }));
    try {
      await getAdminAxios().put(`${API}/admin/clients/${id}`, { password: newPassword });
      setNewPassword("");
      setPasswordSaved(true);
      setTimeout(() => setPasswordSaved(false), 4000);
    } catch (err) {
      setPasswordError(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setSaving((p) => ({ ...p, password: false }));
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-white/5 rounded w-48" />
          <div className="h-96 bg-white/5 rounded-xl" />
        </div>
      </div>
    );
  }

  const permKeys = [
    // Pages access
    { key: "can_access_dashboard", label: "Dashboard", group: "pages" },
    { key: "can_access_connect_whatsapp", label: "Connect WhatsApp", group: "pages" },
    { key: "can_access_inbox", label: "Inbox", group: "pages" },
    { key: "can_access_bot_settings", label: "Bot Settings", group: "pages" },
    { key: "can_access_group_settings", label: "Group Settings", group: "pages" },
    { key: "can_access_replies", label: "Replies", group: "pages" },
    { key: "can_access_group_commands", label: "Group Commands (legacy)", group: "pages" },
    { key: "can_access_groups", label: "Groups", group: "pages" },
    { key: "can_access_bot_studio", label: "Bot Studio", group: "pages" },
    // Modules (the 8 module toggles requested by the user)
    { key: "can_access_private_bot", label: "Bot Privado (chats 1-a-1)", group: "modules" },
    { key: "can_access_ai_replies", label: "Respuestas con IA (Claude)", group: "modules" },
    { key: "can_access_flows", label: "Flow Builder (visual flows)", group: "modules" },
    { key: "can_access_sales", label: "Catálogo / Pedidos / Pagos", group: "modules" },
    { key: "can_access_commands", label: "Comandos personalizados", group: "modules" },
    { key: "can_access_keywords", label: "Palabras Clave", group: "modules" },
    { key: "can_access_broadcast", label: "Difusión masiva (broadcast)", group: "modules" },
    { key: "can_access_media_library", label: "Media Library", group: "modules" },
    { key: "can_access_group_exports", label: "Exportar contactos (Group Sync) — UPGRADE", group: "modules" },
  ];

  const brandingFields = [
    { key: "app_name", label: "App Name", type: "text" },
    { key: "primary_color", label: "Primary Color", type: "color" },
    { key: "secondary_color", label: "Secondary Color", type: "color" },
    { key: "accent_color", label: "Accent Color", type: "color" },
    { key: "login_title", label: "Login Title", type: "text" },
    { key: "login_subtitle", label: "Login Subtitle", type: "text" },
    { key: "sidebar_title", label: "Sidebar Title", type: "text" },
    { key: "dashboard_welcome_text", label: "Welcome Text", type: "text" },
  ];

  return (
    <div className="p-8" data-testid="admin-client-detail">
      <div className="flex items-center gap-4 mb-8">
        <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white" onClick={() => navigate("/admin/clients")} data-testid="back-to-clients">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-white">{client?.name}</h1>
            <Badge className={client?.is_active ? "bg-emerald-500/20 text-emerald-400 border-0" : "bg-red-500/20 text-red-400 border-0"}>
              {client?.is_active ? "Active" : "Inactive"}
            </Badge>
          </div>
          <p className="text-sm text-slate-400">{client?.email} &middot; {client?.business_name}</p>
        </div>
        <Button variant="outline" className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10" onClick={handleImpersonate} data-testid="impersonate-button">
          <LogIn className="w-4 h-4 mr-2" /> Login as Client
        </Button>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="bg-white/5 border border-white/10">
          <TabsTrigger value="profile" className="text-slate-400 data-[state=active]:text-white data-[state=active]:bg-white/10"><User className="w-3.5 h-3.5 mr-1.5" />Profile</TabsTrigger>
          <TabsTrigger value="permissions" className="text-slate-400 data-[state=active]:text-white data-[state=active]:bg-white/10"><Shield className="w-3.5 h-3.5 mr-1.5" />Permissions</TabsTrigger>
          <TabsTrigger value="branding" className="text-slate-400 data-[state=active]:text-white data-[state=active]:bg-white/10"><Palette className="w-3.5 h-3.5 mr-1.5" />Branding</TabsTrigger>
          <TabsTrigger value="whatsapp" className="text-slate-400 data-[state=active]:text-white data-[state=active]:bg-white/10"><Wifi className="w-3.5 h-3.5 mr-1.5" />WhatsApp</TabsTrigger>
          <TabsTrigger value="bot" className="text-slate-400 data-[state=active]:text-white data-[state=active]:bg-white/10"><Bot className="w-3.5 h-3.5 mr-1.5" />Bot</TabsTrigger>
          <TabsTrigger value="group" className="text-slate-400 data-[state=active]:text-white data-[state=active]:bg-white/10"><Users className="w-3.5 h-3.5 mr-1.5" />Group</TabsTrigger>
          <TabsTrigger value="flows" className="text-slate-400 data-[state=active]:text-white data-[state=active]:bg-white/10"><GitBranch className="w-3.5 h-3.5 mr-1.5" />Flows</TabsTrigger>
          <TabsTrigger value="commands" className="text-slate-400 data-[state=active]:text-white data-[state=active]:bg-white/10"><Terminal className="w-3.5 h-3.5 mr-1.5" />Commands</TabsTrigger>
          <TabsTrigger value="groups" className="text-slate-400 data-[state=active]:text-white data-[state=active]:bg-white/10"><UsersRound className="w-3.5 h-3.5 mr-1.5" />Groups</TabsTrigger>
          <TabsTrigger value="workspaces" className="text-slate-400 data-[state=active]:text-white data-[state=active]:bg-white/10" data-testid="tab-workspaces"><Smartphone className="w-3.5 h-3.5 mr-1.5" />WhatsApps</TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card className="bg-[#13112b] border-indigo-900/30">
            <CardHeader><CardTitle className="text-base text-white">Client Profile</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Name</Label>
                  <Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="edit-client-name" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Email</Label>
                  <Input value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="edit-client-email" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Business Name</Label>
                <Input value={profile.business_name} onChange={(e) => setProfile({ ...profile, business_name: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="edit-client-business" />
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500 mt-2">
                <span>Conversations: {client?.conversation_count || 0}</span>
                <span>&middot;</span>
                <span>Messages: {client?.message_count || 0}</span>
                <span>&middot;</span>
                <span>Created: {client?.created_at ? new Date(client.created_at).toLocaleDateString() : "N/A"}</span>
              </div>
              <Button onClick={() => save("profile", "", profile)} disabled={saving.profile} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="save-profile">
                {saving.profile ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Profile
              </Button>

              {/* Password reset */}
              <div className="pt-6 mt-4 border-t border-indigo-900/30">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-4 h-4 text-amber-400" />
                  <h3 className="text-sm font-medium text-white">Reset Password</h3>
                </div>
                <p className="text-xs text-slate-400 mb-3">Set a new password for this client. They will need to use it on their next login.</p>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); setPasswordError(""); setPasswordSaved(false); }}
                    placeholder="New password (min. 6 characters)"
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="admin-new-password-input"
                  />
                  <Button onClick={resetPassword} disabled={saving.password || !newPassword} className="bg-red-500 hover:bg-red-600 text-white shrink-0" data-testid="admin-reset-password-btn">
                    {saving.password ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
                    Set Password
                  </Button>
                </div>
                {passwordError && (
                  <p className="text-xs text-red-400 mt-2" data-testid="admin-password-error">{passwordError}</p>
                )}
                {passwordSaved && (
                  <p className="text-xs text-emerald-400 mt-2" data-testid="admin-password-saved">Password updated successfully.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Permissions Tab */}
        <TabsContent value="permissions">
          <Card className="bg-[#13112b] border-indigo-900/30">
            <CardHeader>
              <CardTitle className="text-base text-white">Workspace Permissions</CardTitle>
              <CardDescription className="text-slate-400">Control which features and modules this client can access. Modules apagados desaparecen del menú lateral del cliente.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-xs uppercase tracking-wider text-amber-400/80 font-semibold mb-2">🛡️ Módulos (los 8 que controlás como Super Admin)</h3>
                {permKeys.filter(p => p.group === "modules").map((p) => (
                  <div key={p.key} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                    <Label className="text-slate-300">{p.label}</Label>
                    <Switch
                      checked={perms[p.key] !== false}
                      onCheckedChange={(checked) => setPerms({ ...perms, [p.key]: checked })}
                      className="data-[state=checked]:bg-amber-500"
                      data-testid={`perm-${p.key}`}
                    />
                  </div>
                ))}
              </div>
              <div>
                <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">📄 Páginas adicionales (avanzado)</h3>
                {permKeys.filter(p => p.group === "pages").map((p) => (
                  <div key={p.key} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                    <Label className="text-slate-300">{p.label}</Label>
                    <Switch
                      checked={perms[p.key] !== false}
                      onCheckedChange={(checked) => setPerms({ ...perms, [p.key]: checked })}
                      className="data-[state=checked]:bg-amber-500"
                      data-testid={`perm-${p.key}`}
                    />
                  </div>
                ))}
              </div>
              <Button onClick={() => save("permissions", "permissions", perms)} disabled={saving.permissions} className="bg-amber-500 hover:bg-amber-600 text-black mt-4" data-testid="save-permissions">
                {saving.permissions ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Permissions
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Branding Tab */}
        <TabsContent value="branding">
          <Card className="bg-[#13112b] border-indigo-900/30">
            <CardHeader>
              <CardTitle className="text-base text-white">Client Branding</CardTitle>
              <CardDescription className="text-slate-400">Customize the look and feel for this client's workspace</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Logo del cliente (arrastrá una imagen, hacé click o pegá una URL)</Label>
                <MediaUploadInput
                  value={branding.logo_url || ""}
                  onChange={(url) => setBranding({ ...branding, logo_url: url })}
                  uploadEndpoint="/admin/upload"
                  uploadAxios={getAdminAxios()}
                  accept="image/*"
                  placeholder="https://... o arrastrá una imagen"
                  testIdPrefix="branding-logo-upload"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                {brandingFields.map((f) => (
                  <div key={f.key} className="space-y-2">
                    <Label className="text-slate-300">{f.label}</Label>
                    {f.type === "color" ? (
                      <div className="flex gap-2 items-center">
                        <input type="color" value={branding[f.key] || "#000000"} onChange={(e) => setBranding({ ...branding, [f.key]: e.target.value })} className="w-10 h-9 rounded border border-white/10 bg-transparent cursor-pointer" data-testid={`branding-${f.key}`} />
                        <Input value={branding[f.key] || ""} onChange={(e) => setBranding({ ...branding, [f.key]: e.target.value })} className="bg-white/5 border-white/10 text-white flex-1" />
                      </div>
                    ) : (
                      <Input value={branding[f.key] || ""} onChange={(e) => setBranding({ ...branding, [f.key]: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid={`branding-${f.key}`} />
                    )}
                  </div>
                ))}
              </div>
              <Button onClick={() => save("branding", "branding", branding)} disabled={saving.branding} className="bg-amber-500 hover:bg-amber-600 text-black mt-4" data-testid="save-branding">
                {saving.branding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Branding
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* WhatsApp Tab */}
        <TabsContent value="whatsapp">
          <Card className="bg-[#13112b] border-indigo-900/30">
            <CardHeader><CardTitle className="text-base text-white">WhatsApp Session</CardTitle></CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 py-4">
                {client?.wa_status === "connected" ? (
                  <><Wifi className="w-8 h-8 text-green-400" /><div><p className="text-white font-medium">Connected</p><p className="text-sm text-slate-400">Phone: +{client?.wa_phone}</p></div></>
                ) : (
                  <><WifiOff className="w-8 h-8 text-slate-500" /><div><p className="text-white font-medium">{(client?.wa_status || "not_connected").replace(/_/g, " ")}</p><p className="text-sm text-slate-400">No active connection</p></div></>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bot Settings Tab */}
        <TabsContent value="bot">
          <Card className="bg-[#13112b] border-indigo-900/30">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base text-white">Bot Settings (Private Chat)</CardTitle>
                <Switch checked={botSettings.enabled || false} onCheckedChange={(v) => setBotSettings({ ...botSettings, enabled: v })} className="data-[state=checked]:bg-amber-500" data-testid="admin-bot-toggle" />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">System Prompt</Label>
                <Textarea value={botSettings.system_prompt || ""} onChange={(e) => setBotSettings({ ...botSettings, system_prompt: e.target.value })} rows={3} className="bg-white/5 border-white/10 text-white" data-testid="admin-system-prompt" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Fallback Reply</Label>
                <Textarea value={botSettings.fallback_reply || ""} onChange={(e) => setBotSettings({ ...botSettings, fallback_reply: e.target.value })} rows={2} className="bg-white/5 border-white/10 text-white" data-testid="admin-fallback-reply" />
              </div>
              <Button onClick={() => save("bot", "bot-settings", botSettings)} disabled={saving.bot} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="save-bot-settings">
                {saving.bot ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Bot Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Group Settings Tab */}
        <TabsContent value="group">
          <Card className="bg-[#13112b] border-indigo-900/30">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base text-white">Group Settings</CardTitle>
                <Switch checked={groupSettings.enabled || false} onCheckedChange={(v) => setGroupSettings({ ...groupSettings, enabled: v })} className="data-[state=checked]:bg-amber-500" data-testid="admin-group-toggle" />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Trigger Mode</Label>
                <div className="grid gap-2">
                  {[
                    { value: "mention_only", label: "Mention Only" },
                    { value: "keyword", label: "Keyword" },
                    { value: "slash_command", label: "Slash Command" },
                  ].map((m) => (
                    <button key={m.value} onClick={() => setGroupSettings({ ...groupSettings, trigger_mode: m.value })}
                      className={`text-left p-3 rounded-lg border transition-colors ${groupSettings.trigger_mode === m.value ? "border-amber-500 bg-amber-500/10" : "border-white/10 hover:border-white/20"}`}
                      data-testid={`admin-trigger-${m.value}`}>
                      <p className="text-sm font-medium text-white">{m.label}</p>
                    </button>
                  ))}
                </div>
              </div>
              {groupSettings.trigger_mode === "keyword" && (
                <div className="space-y-2">
                  <Label className="text-slate-300">Trigger Keyword</Label>
                  <Input value={groupSettings.trigger_keyword || ""} onChange={(e) => setGroupSettings({ ...groupSettings, trigger_keyword: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="admin-trigger-keyword" />
                </div>
              )}
              {groupSettings.trigger_mode === "slash_command" && (
                <div className="space-y-2">
                  <Label className="text-slate-300">Trigger Command</Label>
                  <Input value={groupSettings.trigger_command || ""} onChange={(e) => setGroupSettings({ ...groupSettings, trigger_command: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="admin-trigger-command" />
                </div>
              )}
              <Button onClick={() => save("group", "group-settings", groupSettings)} disabled={saving.group} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="save-group-settings">
                {saving.group ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Group Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Flows Tab */}
        <TabsContent value="flows">
          <Card className="bg-[#13112b] border-indigo-900/30">
            <CardHeader><CardTitle className="text-base text-white">Reply Flows ({flows.length})</CardTitle></CardHeader>
            <CardContent>
              {flows.length === 0 ? <p className="text-sm text-slate-500">No flows configured</p> : (
                <div className="space-y-2">
                  {flows.map((f) => (
                    <div key={f.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                      <div><p className="text-sm text-white font-medium">{f.name}</p><p className="text-xs text-slate-400">Trigger: {f.trigger_keyword} &middot; {f.steps?.length || 0} steps</p></div>
                      <Badge className={f.is_active ? "bg-emerald-500/20 text-emerald-400 border-0" : "bg-slate-500/20 text-slate-400 border-0"}>{f.is_active ? "Active" : "Inactive"}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Commands Tab */}
        <TabsContent value="commands">
          <Card className="bg-[#13112b] border-indigo-900/30">
            <CardHeader><CardTitle className="text-base text-white">Group Commands ({groupCmds.length})</CardTitle></CardHeader>
            <CardContent>
              {groupCmds.length === 0 ? <p className="text-sm text-slate-500">No commands configured</p> : (
                <div className="space-y-2">
                  {groupCmds.map((c) => (
                    <div key={c.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                      <div><code className="text-sm font-bold text-amber-400">{c.command}</code><p className="text-xs text-slate-400 truncate max-w-xs">{c.response_content || c.media_url}</p></div>
                      <Badge className={c.is_active ? "bg-emerald-500/20 text-emerald-400 border-0" : "bg-slate-500/20 text-slate-400 border-0"}>{c.is_active ? "Active" : "Inactive"}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Groups Tab */}
        <TabsContent value="groups">
          <Card className="bg-[#13112b] border-indigo-900/30">
            <CardHeader><CardTitle className="text-base text-white">Selected Groups ({groups.length})</CardTitle></CardHeader>
            <CardContent>
              {groups.length === 0 ? <p className="text-sm text-slate-500">No groups synced</p> : (
                <div className="space-y-2">
                  {groups.map((g) => (
                    <div key={g.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                      <p className="text-sm text-white">{g.group_name}</p>
                      <Badge className={g.is_enabled ? "bg-emerald-500/20 text-emerald-400 border-0" : "bg-slate-500/20 text-slate-400 border-0"}>{g.is_enabled ? "Enabled" : "Disabled"}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Workspaces (multi-WhatsApp) Tab */}
        <TabsContent value="workspaces">
          <ClientWorkspacesPanel
            clientId={id}
            getAdminAxios={getAdminAxios}
            permKeys={permKeys.filter(p => p.group === "modules")}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
